import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { axiosInstance, API } from "../App";
import { toast } from "sonner";
import { ArrowLeft, Plus, Pencil, Trash2, Users, KeyRound, Eye, EyeOff } from "lucide-react";

const KelolaUser = ({ user, onLogout }) => {
  const [users, setUsers] = useState([]);
  const [counters, setCounters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    role: "user",
    counter_id: "",
    nama_lengkap: "",
  });
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, countersRes] = await Promise.all([
        axiosInstance.get(`${API}/users`),
        axiosInstance.get(`${API}/counters`),
      ]);

      setUsers(usersRes.data);
      setCounters(countersRes.data);
    } catch (error) {
      toast.error("Gagal memuat data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.username) {
      toast.error("Username wajib diisi");
      return;
    }

    if (!editMode && !formData.password) {
      toast.error("Password wajib diisi untuk user baru");
      return;
    }

    if (formData.password && formData.password.length < 6) {
      toast.error("Password minimal 6 karakter");
      return;
    }

    try {
      if (editMode) {
        await axiosInstance.put(`${API}/users/${currentUser.id}`, formData);
        toast.success("User berhasil diperbarui");
      } else {
        await axiosInstance.post(`${API}/auth/register`, formData);
        toast.success("User berhasil ditambahkan");
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operasi gagal");
    }
  };

  const handleEdit = (userItem) => {
    setCurrentUser(userItem);
    setFormData({
      username: userItem.username,
      password: "", // Don't populate password for security
      role: userItem.role,
      counter_id: userItem.counter_id || "",
      nama_lengkap: userItem.nama_lengkap,
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Yakin ingin menghapus user ini?")) return;

    try {
      await axiosInstance.delete(`${API}/users/${id}`);
      toast.success("User berhasil dihapus");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Hapus gagal");
    }
  };

  const handleResetPassword = (userItem) => {
    setCurrentUser(userItem);
    setNewPassword("");
    setResetPasswordDialogOpen(true);
  };

  const handleResetPasswordSubmit = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password minimal 6 karakter");
      return;
    }

    try {
      await axiosInstance.put(`${API}/users/${currentUser.id}/reset-password`, {
        new_password: newPassword
      });
      toast.success("Password berhasil direset");
      setResetPasswordDialogOpen(false);
      setNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Reset password gagal");
    }
  };

  const resetForm = () => {
    setFormData({
      username: "",
      password: "",
      role: "user",
      counter_id: "",
      nama_lengkap: "",
    });
    setEditMode(false);
    setCurrentUser(null);
  };

  const getRoleBadge = (role) => {
    return role === "admin" 
      ? <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">Admin</span>
      : <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">User</span>;
  };

  const getCounterName = (counterId) => {
    const counter = counters.find(c => c.id === counterId);
    return counter ? counter.nama_counter : "-";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Memuat...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Link to="/">
                <Button variant="ghost" size="icon" className="hover:bg-gray-100">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <img src="/logo-dumont.jpg" alt="Dumont" className="h-12 w-auto" />
              <div className="border-l border-gray-300 pl-3">
                <h1 className="text-2xl font-bold text-gray-800">Kelola User</h1>
                <p className="text-sm text-gray-600">Manajemen pengguna sistem</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className="px-3 py-1 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">
                {user?.nama_lengkap}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="shadow-lg">
          <CardHeader className="border-b bg-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <CardTitle className="text-xl">Daftar User</CardTitle>
                  <p className="text-sm text-gray-500 mt-1">Total: {users.length} user</p>
                </div>
              </div>
              <Button
                onClick={() => {
                  resetForm();
                  setDialogOpen(true);
                }}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Tambah User
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Username</TableHead>
                    <TableHead>Nama Lengkap</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Counter</TableHead>
                    <TableHead className="text-right">Aksi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((userItem) => (
                    <TableRow key={userItem.id}>
                      <TableCell className="font-medium">{userItem.username}</TableCell>
                      <TableCell>{userItem.nama_lengkap}</TableCell>
                      <TableCell>{getRoleBadge(userItem.role)}</TableCell>
                      <TableCell>{getCounterName(userItem.counter_id)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end space-x-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleResetPassword(userItem)}
                            className="hover:bg-yellow-50 hover:text-yellow-600"
                            title="Reset Password"
                          >
                            <KeyRound className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(userItem)}
                            className="hover:bg-blue-50 hover:text-blue-600"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(userItem.id)}
                            className="hover:bg-red-50 hover:text-red-600"
                            disabled={userItem.id === user?.id}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editMode ? "Edit User" : "Tambah User Baru"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="username">Username *</Label>
                <Input
                  id="username"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="Username untuk login"
                  required
                />
              </div>

              <div>
                <Label htmlFor="nama_lengkap">Nama Lengkap (Optional)</Label>
                <Input
                  id="nama_lengkap"
                  value={formData.nama_lengkap}
                  onChange={(e) => setFormData({ ...formData, nama_lengkap: e.target.value })}
                  placeholder="Kosongkan jika sales akan isi sendiri"
                />
                <p className="text-xs text-gray-500 mt-1">Sales bisa isi nama sendiri saat input nota pertama kali</p>
              </div>

              <div>
                <Label htmlFor="password">Password {!editMode && "*"}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder={editMode ? "Kosongkan jika tidak ingin mengubah" : "Minimal 6 karakter"}
                    required={!editMode}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
                {editMode && <p className="text-xs text-gray-500 mt-1">Kosongkan jika tidak ingin mengubah password</p>}
              </div>

              <div>
                <Label htmlFor="role">Role *</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Pilih role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="counter">Counter (Optional)</Label>
                <Select value={formData.counter_id || "none"} onValueChange={(value) => setFormData({ ...formData, counter_id: value === "none" ? "" : value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Pilih counter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Tidak Ada</SelectItem>
                    {counters.map((counter) => (
                      <SelectItem key={counter.id} value={counter.id}>
                        {counter.nama_counter}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Batal
              </Button>
              <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                {editMode ? "Update" : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <p className="text-sm text-gray-600 mb-4">
                Reset password untuk: <span className="font-semibold">{currentUser?.username}</span>
              </p>
              <Label htmlFor="new_password">Password Baru *</Label>
              <div className="relative">
                <Input
                  id="new_password"
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimal 6 karakter"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  tabIndex={-1}
                >
                  {showNewPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setResetPasswordDialogOpen(false)}>
              Batal
            </Button>
            <Button onClick={handleResetPasswordSubmit} className="bg-yellow-600 hover:bg-yellow-700">
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KelolaUser;
